/**
 * WHAT A TEACHER HAS TURNED ON, declared once.
 *
 * The builder is feature-complete enough that meeting all of it at once is the problem. These are
 * the capabilities a teacher opts into, one at a time, each with its own explanation — rather than
 * beginner/intermediate/advanced modes, which would force an arbitrary ruling on whether (say)
 * answer recall is intermediate or advanced AND couple unrelated features together.
 *
 * ## Off means HIDDEN, never forbidden
 *
 * A capability that is off removes an AUTHORING AFFORDANCE. It never changes what a document does:
 * content that already uses the capability still renders, still grades, still runs for students.
 * So every gate goes on an entry point, and no resolver, renderer or grader ever consults one.
 *
 * This is the opposite of `mode-gated-settings`, and the two must not be confused:
 *
 * - **Mode-gated** — present, ghosted, and INERT. `effectiveSetting` forces every reader to ignore
 *   the stored value and `CheckboxRow` forces the control to read as off, because a grading mode
 *   genuinely suppresses the behaviour.
 * - **Capability-gated** — ABSENT, and fully active. The content keeps working; the teacher simply
 *   has no control for it here.
 *
 * Reusing the ghost treatment for a capability would produce one of two bugs: a control claiming
 * "off" about a document that uses the feature, or a gate that silently changes what students see.
 * It is also already taken — a ghosted row in this app means "turn on the thing above this", which
 * is fixed in place, whereas a capability is fixed in Settings.
 *
 * ## Stacking
 *
 * Prerequisites are declared, never inferred, and turning a capability on raises its prerequisites
 * with it (`withPrerequisites`). Lowering one is refused while something above it still needs it
 * (`blockingDependents`) — the same disable-and-explain shape as `BlockDeleteGuard` and the
 * variables panel's `blockedReason`, rather than accepting and complaining afterwards.
 *
 * ## Not here
 *
 * Formula and reaction CATEGORY visibility is a preference, not a capability: hiding a category
 * prevents no bad outcome, the categories are not linearly ordered (an organic chemist and a
 * particle physicist want different ones, not more or fewer), and every symbol stays reachable by
 * typing. It needs no prerequisite, no census and no lock, so it does not belong in this graph.
 */

export type CapabilityId =
  | 'questionVersions'
  | 'scenarios'
  | 'flow'
  | 'flowChanges'
  | 'variables'
  | 'answerRecall'
  | 'extraBlockSettings';

/**
 * A prerequisite, in force from `fromRung` upward.
 *
 * `{ id: 'questionVersions', fromRung: 1 }` on `scenarios` reads: once scenarios reaches rung 1,
 * questionVersions must be at `atLeast` (default 1). A `fromRung` above 1 is what lets the lower
 * rungs of a ladder stand alone while only its top rung carries a prerequisite.
 */
export interface Prerequisite {
  id: CapabilityId;
  fromRung: number;
  /** The rung the prerequisite must itself reach. Defaults to 1 (simply on). */
  atLeast?: number;
}

export interface CapabilityDef {
  id: CapabilityId;
  /**
   * The rungs, in order, as stored levels 1..n. Level 0 is always off.
   *
   * A plain on/off capability declares exactly one rung, so the same shape covers both and the
   * page does not need two kinds of row.
   */
  rungs: readonly string[];
  requires?: readonly Prerequisite[];
}

/**
 * The graph.
 *
 * ## Branching corrects is NOT its own node
 *
 * It was, briefly, on the reasoning that the mechanism is a control equation that selects among
 * answer setups — which a recall chip or bare arithmetic satisfies as well as a variable does. The
 * code says otherwise: every branching block gates the toggle on
 * `showConditionalUI = hasVariable && gradingMode === 'graded'`, and `hasVariable` is
 * `htmlHasVariableChips` — VARIABLE chips only. A recall chip does not open it.
 *
 * So branching corrects is what the top rung of `variables` MEANS, not a sibling of it. If recall
 * ever learns to drive a control equation, that is the change that would make it a node again.
 */
export const CAPABILITIES: readonly CapabilityDef[] = [
  /*
   * FIRST, because it is the least conceptually expensive thing here: more settings on blocks the
   * teacher is already using, with no new structure and no new mental model. Everything below it
   * asks the teacher to hold something in their head that the screen is not showing them.
   */
  { id: 'extraBlockSettings', rungs: ['on'] },

  { id: 'questionVersions', rungs: ['on'] },
  { id: 'scenarios', rungs: ['on'], requires: [{ id: 'questionVersions', fromRung: 1 }] },

  /*
   * ONE rung. A split between routing on a task outcome and routing on a block's feedback state
   * was proposed and does not exist: `anyBlockIs` / `everyBlockIs` are the HINT TEXT under the
   * built-in All Correct and Any Incomplete rungs, describing them in terms of block states
   * rather than offering a second mode. Flow's difficulty is how many events a task accumulates,
   * which is not something a rung can gate.
   */
  { id: 'flow', rungs: ['on'] },
  { id: 'flowChanges', rungs: ['on'], requires: [{ id: 'flow', fromRung: 1 }] },

  /** The top rung is what opens the branching-correct toggle on all 8 branching blocks. */
  { id: 'variables', rungs: ['numerical', 'sensors', 'everywhere'] },
  /**
   * Deliberately NOT requiring questionVersions at rung 2.
   *
   * Recalling a choice drives versions, but linking generates the version set from the leader's
   * options — the teacher never adds one, names one, or invents a purpose for an empty bank. So it
   * needs the machinery, not the capability. The consequence lands on the CENSUS rather than here:
   * a usage scan for questionVersions must count versions on blocks WITHOUT `drivenBy`, or
   * enabling recall would lock a capability its owner never turned on.
   */
  /*
   * THREE rungs, and it was four. Two of those gated things the engine does not have, which the
   * owner spotted before the gate was built:
   *
   *   - "mixed-type aliases" (old rung 4) are impossible at EVERY rung. `aliasFormationFaults`
   *     raises `typeMismatch` the moment members span two block types, and Numerical with Slider
   *     is refused by name even though both answer with a number.
   *   - "into a block of its own kind" (old rung 3) is not a rule anywhere. The only kind filter
   *     is `FacetKindFilter = 'any' | 'number'`, applied by the DESTINATION — a formula field
   *     asks for numbers, prose takes anything — so "where the kinds get mixed" is already a
   *     type rule rather than a capability.
   *
   * And multi-member aliases need no rung of their own. Exclusivity needs two routes, so on a
   * plain spine no pair of tasks qualifies, the add-member picker disables every candidate, and
   * the manager drops the control entirely — self-gated by Flow, pinned in
   * `alias-formation.test.ts`. A rung for them would also SUBTRACT: it would withhold the base
   * case the feature exists for, which is the same question asked on two sister pathways.
   *
   *   1 numerical   a numerical answer into a numerical question — error carried forward, and the
   *                 answer equation that resolves it already exists
   *   2 sensors     the same, into any block whose answer is a value — adds facets, since a
   *                 compass or GPS answer resolves to a pair or a point rather than a scalar
   *   3 text        text and choice answers, and DRIVEN-BY with them. They travel together on
   *                 purpose: a text answer can change the correct answer, so a teacher who gets
   *                 text recall gets the load-bearing form with it rather than meeting a
   *                 decorative one that turns load-bearing later.
   */
  { id: 'answerRecall', rungs: ['numerical', 'sensors', 'text'] },
];

const BY_ID = new Map(CAPABILITIES.map((c) => [c.id, c]));

export function capabilityDef(id: CapabilityId): CapabilityDef {
  const def = BY_ID.get(id);
  if (!def) throw new Error(`Unknown capability: ${id}`);
  return def;
}

/** Stored as level numbers: 0 is off, 1..rungs.length is the rung in force. */
export type CapabilityState = Partial<Record<CapabilityId, number>>;

export function levelOf(state: CapabilityState, id: CapabilityId): number {
  return state[id] ?? 0;
}

export function isEnabled(state: CapabilityState, id: CapabilityId, atLeast = 1): boolean {
  return levelOf(state, id) >= atLeast;
}

/** Everything at its top rung. */
export const ALL_ON: CapabilityState = Object.fromEntries(
  CAPABILITIES.map((c) => [c.id, c.rungs.length]),
) as CapabilityState;

/** Everything off. */
export const ALL_OFF: CapabilityState = Object.fromEntries(
  CAPABILITIES.map((c) => [c.id, 0]),
) as CapabilityState;

function prereqsInForce(id: CapabilityId, rung: number): Prerequisite[] {
  return (capabilityDef(id).requires ?? []).filter((p) => rung >= p.fromRung);
}

/**
 * The prerequisites a move to `rung` would not yet satisfy, transitively.
 *
 * Returned so the page can name all of them in ONE explanation rather than making the teacher
 * discover them a refusal at a time.
 */
export function unmetPrerequisites(
  state: CapabilityState, id: CapabilityId, rung: number,
): { id: CapabilityId; rung: number }[] {
  const needed = new Map<CapabilityId, number>();
  const walk = (capId: CapabilityId, atRung: number) => {
    for (const p of prereqsInForce(capId, atRung)) {
      const want = p.atLeast ?? 1;
      if (levelOf(state, p.id) >= want) continue;        // the state already satisfies it
      if ((needed.get(p.id) ?? 0) >= want) continue;     // already recorded at least this high
      needed.set(p.id, want);
      walk(p.id, want);
    }
  };
  walk(id, rung);
  return [...needed].map(([capId, r]) => ({ id: capId, rung: r }));
}

/** The state after moving `id` to `rung`, with every prerequisite raised to meet it. */
export function withPrerequisites(
  state: CapabilityState, id: CapabilityId, rung: number,
): CapabilityState {
  const next = { ...state, [id]: rung };
  for (const p of unmetPrerequisites(state, id, rung)) next[p.id] = p.rung;
  return next;
}

/**
 * What would break if `id` dropped to `rung`, and therefore why the move is refused.
 *
 * Empty means the move is allowed. Non-empty is the REASON, in the app's house style: disable the
 * control and say what holds it, rather than accepting the click and complaining after.
 */
export function blockingDependents(
  state: CapabilityState, id: CapabilityId, rung: number,
): CapabilityId[] {
  const blockers: CapabilityId[] = [];
  for (const def of CAPABILITIES) {
    if (def.id === id) continue;
    const at = levelOf(state, def.id);
    if (at === 0) continue;
    for (const p of prereqsInForce(def.id, at)) {
      if (p.id === id && rung < (p.atLeast ?? 1)) blockers.push(def.id);
    }
  }
  return [...new Set(blockers)];
}

/**
 * What a stored record means, resolved in ONE place.
 *
 * `null` is a teacher who has never opened the page, and they start with EVERYTHING OFF. That is
 * the whole point of the feature — a new teacher meets one capability at a time — and until this
 * line said so, the beginner's view existed only for someone who went to Settings and switched
 * things off, which is not a beginner.
 *
 * It resolved to `ALL_ON` while the gates were being built, so that an account which predated them
 * could not silently lose access to what it was already authoring with. That protection is no
 * longer needed: the owner confirmed on 2026-10-31 that there are no real accounts yet, so there
 * is nothing to migrate and no one to strand.
 *
 * A capability turned off and a capability never chosen are therefore the same thing, which is
 * what lets this stay one function rather than a flag. If real accounts ever need the old
 * behaviour, they get an explicit `ALL_ON` record written at migration time — NOT a second
 * meaning for `null` here, which would put the question back in two places.
 */
export function resolveStoredCapabilities(stored: CapabilityState | null | undefined): CapabilityState {
  if (!stored) return ALL_OFF;
  return clampToPrerequisites(clampToLadders(stored));
}

/**
 * A STORED LEVEL ABOVE ITS LADDER IS CLAMPED TO THE TOP OF IT.
 *
 * Answer Recall had four rungs and now has three, and a record written before that says `4`. The
 * settings page renders one switch per rung and decides what a teacher may press from the level:
 * on is available from `rung - 1`, off from `rung` exactly. A level of 4 against rungs 1, 2 and 3
 * matches neither test for any of them, so EVERY switch reads held and the capability cannot be
 * turned off at all. Reported as exactly that: "unlike the others, i can't turn the gating off".
 *
 * Clamped here rather than migrated in the database, for three reasons. This is the one funnel
 * every reader goes through, so it fixes the gates and the page together. A migration fixes
 * today and not the next rung that is removed, nor a record written by a deploy on either side
 * of the change. And it keeps the teacher's INTENT: a stored 4 meant "all of it", and the top of
 * the ladder is still all of it.
 *
 * Only downward. A level below its ladder is an ordinary rung and means what it says.
 */
function clampToLadders(stored: CapabilityState): CapabilityState {
  let changed = false;
  const out = { ...stored } as Record<string, number>;
  for (const def of CAPABILITIES) {
    const top = def.rungs.length;
    const at = out[def.id];
    if (typeof at === 'number' && at > top) { out[def.id] = top; changed = true; }
  }
  // The same object back where nothing moved, so an identity check upstream stays meaningful.
  return changed ? (out as unknown as CapabilityState) : stored;
}

/**
 * A DEPENDENT WHOSE PREREQUISITE IS OFF DROPS TO THE RUNG ITS PREREQUISITES ALLOW.
 *
 * The same class of fault as the ladder clamp above, found by auditing the switches against each
 * other rather than one at a time: `clampToLadders` makes every LEVEL legal and nothing made the
 * COMBINATION legal. A record holding `{ scenarios: 1, questionVersions: 0 }` resolved to itself
 * untouched, and a surface is already relying on that pair being impossible —
 * `version-selector` offers scenario authoring on the strength of the graph alone, so an
 * incoherent state has it authoring a scenario bank on a block whose question bank it refuses.
 *
 * The settings page cannot produce one: `canTurnOn` refuses until the prerequisites are met and
 * `canTurnOff` is blocked by `blockingDependents`. The hole is the same one the stored rung 4
 * came through — a REGISTRY that changes under records already written. Add a `requires` edge to
 * a capability that did not have one, or write a record from either side of that deploy, and
 * every account holding the old pair is in a state no screen can reach or explain.
 *
 * DOWNWARD, like the clamp above, and that is the whole argument: raising the prerequisite would
 * hand the teacher a capability they never turned on, which is the one thing this feature exists
 * not to do. Lowering costs them nothing they can see — every gate in the app keeps what is
 * already authored (see `useExtraBlockSetting`, `mayFollowScenario`, `useDrivenAllowed`), so a
 * dropped rung withholds the `+` and strands nothing.
 *
 * To a FIXED POINT, which today is DEFENSIVE rather than load-bearing: the only chain is
 * `flowChanges` -> `flow`, and `flow` is registered first, so one pass settles it. The loop earns
 * its keep against a chain three deep or a registry reordered — neither of which a test can reach
 * from the registry as it stands, so no test claims to.
 */
function clampToPrerequisites(stored: CapabilityState): CapabilityState {
  let out = stored;
  let changed = true;
  while (changed) {
    changed = false;
    for (const def of CAPABILITIES) {
      const at = levelOf(out, def.id);
      if (at === 0) continue;
      let rung = at;
      while (rung > 0 && unmetPrerequisites(out, def.id, rung).length > 0) rung -= 1;
      if (rung !== at) {
        out = { ...out, [def.id]: rung };
        changed = true;
      }
    }
  }
  return out;
}
